/**
 * @module app.shared
 */

export interface IChunkedFileReaderOptions {
    chunkSize: number;
    contentType?: string;
}

const defaultOptions: IChunkedFileReaderOptions = {
    chunkSize: 256 * 1024 // 256KB, (0.25 MB)
}

function dataUrlToBase64(dataUrl: string): string {
    return dataUrl.substr(dataUrl.indexOf(',') + 1);
}

export class ChunkedFileReader {

    private readonly options: IChunkedFileReaderOptions;
    private offset: number = 0;

    constructor(private readonly file: Blob, options?: Partial<IChunkedFileReaderOptions>) {
        this.options = { ...defaultOptions, ...options };

        if(!this.options.contentType && file instanceof File) {
            this.options.contentType = file.type;
        }
    }

    public isComplete(): boolean {
        return (this.offset >= this.file.size);
    }

    public calculateProgress(): number {
        return Math.min(1.0, this.offset / this.file.size);
    }

    public async readBase64(): Promise<string> {
        if(this.isComplete()) { return ''; }

        const slice = this.file.slice(this.offset, this.offset + this.options.chunkSize);
        this.offset = Math.min(this.offset + this.options.chunkSize, this.file.size);

        const dataUrl = await this.readAsDataUrl(slice);
        return dataUrlToBase64(dataUrl);
    }

    private readAsDataUrl(blob: Blob): Promise<string> {
        const reader = new FileReader();

        return new Promise((resolve, reject) => {
            reader.onerror = () => {
                reader.abort();
                reject(new Error("Problem parsing input file."));
            };
            reader.onload = () => {
                resolve(<string>reader.result);
            };
            reader.readAsDataURL(blob);
        });
    }
}

export function openFileInWindow(targetWindow: Window, file: Blob): void {
    if(!targetWindow || targetWindow.closed) { return; }

    const url = window.URL.createObjectURL(file);

    // check periodically to see if window has closed
    const handle = window.setInterval(() => {
        if(targetWindow.closed) {
            // revoke URL and stop checking
            window.clearInterval(handle);
            window.URL.revokeObjectURL(url);
        }
    }, 5000); //5 seconds

    targetWindow.location.replace(url);
}

export function downloadFile(file: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(file);
    try {
        const link = document.createElement('a');
        link.style.display = 'none';
        link.href = url;
        link.setAttribute('download', fileName);
        if(!link.download) {
            link.setAttribute('target', '_blank');
        }
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
    finally {
        setTimeout(() => { window.URL.revokeObjectURL(url); }, 100);
    }
}
